import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import type { JSX } from "react";

/**
 * Generic create/edit dialog for the product catalogue. The Product,
 * Feature, and Version dialogs were three near-identical blocks in
 * `ProductsPanel.tsx`; they now all render through this one shell, driven
 * by a `fields` list. The submit button lives in `DialogActions` and is
 * wired to the form via the `form` attribute (so the form's `onSubmit`
 * fires on Enter or click).
 */
export type FieldSpec = {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
};

export function EntityFormDialog(props: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  formId: string;
  fields: FieldSpec[];
  onSubmit: () => void;
  isPending: boolean;
  submitLabel: string;
  error: string | null;
}): JSX.Element {
  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm">
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>{props.description}</DialogContentText>
        <Box
          component="form"
          id={props.formId}
          sx={{ display: "grid", gap: 1.5, mt: 2 }}
          onSubmit={(e) => {
            e.preventDefault();
            props.onSubmit();
          }}
        >
          {props.fields.map((f) => (
            <Field key={f.id} {...f} />
          ))}
          {props.error && (
            <Typography sx={{ fontSize: 14, color: "error.main" }}>{props.error}</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" type="button" onClick={props.onClose}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" form={props.formId} disabled={props.isPending}>
          {props.submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Field(props: FieldSpec): JSX.Element {
  return (
    <TextField
      id={props.id}
      label={props.label}
      size="small"
      fullWidth
      value={props.value}
      required={props.required ?? false}
      // The label text already embeds its own "*" (e.g. "Name *"), so
      // suppress MUI's auto-appended asterisk — otherwise the accessible
      // name doubles to "Name * *" and breaks getByLabelText("Name *").
      // `required` still flows to the <input> for form semantics.
      InputLabelProps={{ required: false }}
      placeholder={props.placeholder ?? ""}
      onChange={(e) => props.onChange(e.target.value)}
    />
  );
}
